import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SEOMeta from '../components/SEOMeta';

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const Header = styled.header`
  background: white;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
`;

const Content = styled.div`
  padding: 24px 16px;
  background: white;
  min-height: calc(100dvh - 60px);
`;

const IntroText = styled.p`
  font-size: 15px;
  color: #4b5563;
  line-height: 1.6;
  margin: 0 0 24px;
  text-align: center;
`;

const ContactCard = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
`;

const ContactIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(45, 188, 175, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 24px;
`;

const ContactLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const ContactValue = styled.a`
  font-size: 15px;
  font-weight: 600;
  color: #2DBCAF;
  text-decoration: none;
  display: block;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ContactText = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1a1f2e;
`;

const AddressText = styled.div`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
`;

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 24px 0;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScheduleCard = styled.div`
  background: linear-gradient(135deg, #2DBCAF 0%, #059669 100%);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  color: white;
`;

const ScheduleTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScheduleItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 6px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ScheduleDay = styled.span`
  opacity: 0.9;
`;

const ScheduleTime = styled.span`
  font-weight: 600;
`;

const ScheduleNote = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 12px;
  font-style: italic;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 18px;
  text-decoration: none;
  flex: 1;
  transition: background 0.2s;
  
  &:hover {
    background: #f1f5f9;
  }
`;

const SocialIcon = styled.span`
  font-size: 24px;
`;

const SocialInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SocialName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #1a1f2e;
`;

const SocialHandle = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const TipBox = styled.div`
  background: #fef3c7;
  border-radius: 12px;
  padding: 16px;
  margin-top: 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const TipIcon = styled.span`
  font-size: 20px;
`;

const TipText = styled.p`
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
  margin: 0;
  
  a {
    color: #b45309;
    font-weight: 600;
  }
`;

const ContactPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <SEOMeta 
        title="Hubungi Kami | KinderGame.id"
        description="Hubungi tim Kindergame untuk pertanyaan dan bantuan"
      />
      
      <Header>
        <BackButton onClick={() => navigate(-1)}>←</BackButton>
        <HeaderTitle>Kontak Kami</HeaderTitle>
      </Header>

      <Content>
        <IntroText>
          Butuh bantuan? Tim Kindergame siap membantu Anda!
        </IntroText>

        <ContactCard>
          <ContactIcon>📧</ContactIcon>
          <ContactLabel>Pertanyaan Umum & Support</ContactLabel>
          <ContactValue href="mailto:support@kindergame.id">
            support@kindergame.id
          </ContactValue>
        </ContactCard>

        <ContactCard>
          <ContactIcon>🤝</ContactIcon>
          <ContactLabel>Kerjasama Bisnis</ContactLabel>
          <ContactValue href="mailto:partnership@kindergame.id">
            partnership@kindergame.id
          </ContactValue>
        </ContactCard>

        <ContactCard>
          <ContactIcon>📱</ContactIcon>
          <ContactLabel>Telepon</ContactLabel>
          <ContactText>087788116467</ContactText>
        </ContactCard>

        <ScheduleCard>
          <ScheduleTitle>
            <span>⏰</span> Jam Operasional
          </ScheduleTitle>
          <ScheduleItem>
            <ScheduleDay>Senin - Jumat</ScheduleDay>
            <ScheduleTime>09:00 - 17:00 WIB</ScheduleTime>
          </ScheduleItem>
          <ScheduleItem>
            <ScheduleDay>Sabtu</ScheduleDay>
            <ScheduleTime>09:00 - 12:00 WIB</ScheduleTime>
          </ScheduleItem>
          <ScheduleItem>
            <ScheduleDay>Minggu & Hari Libur</ScheduleDay>
            <ScheduleTime>Tutup</ScheduleTime>
          </ScheduleItem>
          <ScheduleNote>
            *Pesan di luar jam operasional akan dibalas pada hari kerja berikutnya
          </ScheduleNote>
        </ScheduleCard>

        <ContactCard>
          <ContactIcon>🏢</ContactIcon>
          <ContactLabel>Alamat Kantor</ContactLabel>
          <ContactText>PT Dewangga Bersaudara Jaya Bersama</ContactText>
          <AddressText>
            Griya Kisanda Asri no A2<br />
            Kab Malang, 65152<br />
            Indonesia
          </AddressText>
        </ContactCard>

        <Divider />

        <SectionTitle>🌐 Media Sosial</SectionTitle>
        
        <SocialLinks>
          <SocialLink href="https://www.instagram.com/kindergame.id/" target="_blank" rel="noopener noreferrer">
            <SocialIcon>📸</SocialIcon>
            <SocialInfo>
              <SocialName>Instagram</SocialName>
              <SocialHandle>@kindergame.id</SocialHandle>
            </SocialInfo>
          </SocialLink>
          
          <SocialLink href="https://www.tiktok.com/@kindergame.id" target="_blank" rel="noopener noreferrer">
            <SocialIcon>🎵</SocialIcon>
            <SocialInfo>
              <SocialName>TikTok</SocialName>
              <SocialHandle>@kindergame.id</SocialHandle>
            </SocialInfo>
          </SocialLink>
        </SocialLinks>

        <TipBox>
          <TipIcon>💡</TipIcon>
          <TipText>
            Sudah cek halaman <a href="/faq">FAQ</a>? Banyak pertanyaan umum sudah terjawab di sana!
          </TipText>
        </TipBox>
      </Content>
    </PageWrapper>
  );
};

export default ContactPage;
